import useSWR, { mutate } from 'swr';
import api from '../../services/api';

export interface KnowledgeBaseCategory {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  displayOrder: number;
  isActive: boolean;
  parentId?: number | null;
  parent?: { id: number; name: string };
  subcategories?: { id: number; name: string; displayOrder: number }[];
  _count?: {
    articles: number;
  };
  createdAt: string;
  updatedAt: string;
}

/** Tree node for sidebar (like email personal folders): nested children */
export interface KnowledgeBaseCategoryTreeNode {
  id: number;
  name: string;
  icon?: string;
  color?: string;
  displayOrder: number;
  isActive: boolean;
  parentId?: number | null;
  children: KnowledgeBaseCategoryTreeNode[];
  articleCount?: number;
}

/** Build a tree from flat categories (roots first, then nested children) */
export function buildCategoryTree(categories: KnowledgeBaseCategory[]): KnowledgeBaseCategoryTreeNode[] {
  const list = [...categories].sort(
    (a, b) => a.displayOrder - b.displayOrder || (a.name || '').localeCompare(b.name || '')
  );
  const byId = new Map<number, KnowledgeBaseCategoryTreeNode>();
  list.forEach((c) => {
    byId.set(c.id, {
      id: c.id,
      name: c.name,
      icon: c.icon,
      color: c.color,
      displayOrder: c.displayOrder,
      isActive: c.isActive ?? true,
      parentId: c.parentId ?? null,
      children: [],
      articleCount: c._count?.articles ?? 0,
    });
  });
  const roots: KnowledgeBaseCategoryTreeNode[] = [];
  byId.forEach((node) => {
    if (node.parentId == null) {
      roots.push(node);
    } else {
      const parent = byId.get(node.parentId);
      if (parent) parent.children.push(node);
      else roots.push(node);
    }
  });
  roots.sort((a, b) => a.displayOrder - b.displayOrder || (a.name || '').localeCompare(b.name || ''));
  byId.forEach((node) => {
    node.children.sort((a, b) => a.displayOrder - b.displayOrder || (a.name || '').localeCompare(b.name || ''));
  });
  return roots;
}

export interface KnowledgeBaseArticle {
  id: number;
  title: string;
  slug: string;
  summary?: string;
  content: string;
  contentText?: string;
  categoryId?: number | null;
  category?: KnowledgeBaseCategory;
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  isFeatured: boolean;
  isPinned: boolean;
  viewCount: number;
  lastViewedAt?: string;
  version: number;
  parentArticleId?: number;
  createdAt: string;
  createdById?: number;
  createdBy?: {
    id: number;
    userDisplayName?: string;
    userFirstName?: string;
    userLastName?: string;
  };
  updatedAt: string;
  updatedById?: number;
  updatedBy?: {
    id: number;
    userDisplayName?: string;
    userFirstName?: string;
    userLastName?: string;
  };
  publishedAt?: string;
  attachments?: Array<{
    id: number;
    fileName?: string | null;
    filePath?: string | null;
    fileType?: string | null;
    fileSize?: number | null;
    uploadedAt?: string | null;
  }>;
  relatedArticles?: {
    target: KnowledgeBaseArticle;
    relationType: string;
  }[];
  _count?: {
    relatedArticles: number;
    attachments: number;
  };
}

export interface SearchFilters {
  categoryId?: number;
  tags?: string[];
  status?: string;
  isFeatured?: boolean;
  isPinned?: boolean;
}

// Fetch all articles
export const useKnowledgeBaseArticles = (filters?: SearchFilters) => {
  const queryParams = new URLSearchParams();
  if (filters?.categoryId) queryParams.append('categoryId', filters.categoryId.toString());
  if (filters?.tags) filters.tags.forEach(tag => queryParams.append('tags', tag));
  if (filters?.status) queryParams.append('status', filters.status);
  if (filters?.isFeatured !== undefined) queryParams.append('isFeatured', filters.isFeatured.toString());
  if (filters?.isPinned !== undefined) queryParams.append('isPinned', filters.isPinned.toString());

  const url = `/knowledge-base${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const { data, error, isLoading, mutate } = useSWR<KnowledgeBaseArticle[]>(
    url,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    error,
    mutate,
  };
};

// Fetch a single article
export const useKnowledgeBaseArticle = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<KnowledgeBaseArticle>(
    id ? `/knowledge-base/${id}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data,
    client: data, // GenericFormPage expects either data or client
    isLoading,
    error,
    mutate,
  };
};

// Shared fetcher so list and categories module share the same cache shape (array). Exported for categories/useData.
export const fetchKnowledgeBaseCategories = async (url: string): Promise<KnowledgeBaseCategory[]> => {
  const response = await api.get(url);
  const raw = response.data;
  return Array.isArray(raw) ? raw : (raw?.data ?? []);
};

// Fetch all categories (same key & shape as categories/useData so cache stays consistent)
export const useKnowledgeBaseCategories = () => {
  const { data, error, isLoading, mutate } = useSWR<KnowledgeBaseCategory[]>(
    '/knowledge-base/categories',
    fetchKnowledgeBaseCategories,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    error,
    mutate,
  };
};

// Search articles
export const useSearchKnowledgeBase = (query: string, filters?: SearchFilters) => {
  const searchParams = new URLSearchParams();
  if (query) searchParams.append('query', query);
  if (filters?.categoryId) searchParams.append('categoryId', filters.categoryId.toString());
  if (filters?.tags) filters.tags.forEach(tag => searchParams.append('tags', tag));
  if (filters?.status) searchParams.append('status', filters.status);
  if (filters?.isFeatured !== undefined) searchParams.append('isFeatured', filters.isFeatured.toString());
  if (filters?.isPinned !== undefined) searchParams.append('isPinned', filters.isPinned.toString());

  const url = `/knowledge-base/search?${searchParams.toString()}`;

  const { data, error, isLoading, mutate } = useSWR<{ articles: KnowledgeBaseArticle[]; total: number; limit: number; offset: number }>(
    query || Object.keys(filters || {}).length > 0 ? url : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    data: data?.articles || [],
    total: data?.total || 0,
    isLoading,
    error,
    mutate,
  };
};

// Create a new article
export const createKnowledgeBaseArticle = async (
  articleData: Partial<KnowledgeBaseArticle>,
  files?: File[],
  _filesToDetach?: number[],
): Promise<KnowledgeBaseArticle> => {
  if (files?.length) {
    const formData = new FormData();
    formData.append('json_data', JSON.stringify(articleData));
    files.forEach((file) => formData.append('files', file));
    const response = await api.post('/knowledge-base', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    await mutate('/knowledge-base');
    await mutate('/knowledge-base/categories');
    return response.data;
  }
  const response = await api.post('/knowledge-base', articleData);
  await mutate('/knowledge-base');
  await mutate('/knowledge-base/categories');
  return response.data;
};

// Update an article
export const updateKnowledgeBaseArticle = async (
  id: number,
  articleData: Partial<KnowledgeBaseArticle>,
  files?: File[],
  filesToDetach?: number[],
): Promise<KnowledgeBaseArticle> => {
  if (files?.length || filesToDetach?.length) {
    const formData = new FormData();
    formData.append('json_data', JSON.stringify(articleData));
    if (filesToDetach?.length) {
      formData.append('filesToDetach', JSON.stringify(filesToDetach));
    }
    if (files?.length) {
      files.forEach((file) => formData.append('files', file));
    }
    const response = await api.patch(`/knowledge-base/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    await mutate(`/knowledge-base/${id}`);
    await mutate('/knowledge-base');
    return response.data;
  }
  const response = await api.patch(`/knowledge-base/${id}`, articleData);
  await mutate(`/knowledge-base/${id}`);
  await mutate('/knowledge-base');
  return response.data;
};

// Delete an article
export const deleteKnowledgeBaseArticle = async (id: number): Promise<void> => {
  await api.delete(`/knowledge-base/${id}`);
  await mutate('/knowledge-base');
};

// Publish an article
export const publishKnowledgeBaseArticle = async (id: number): Promise<KnowledgeBaseArticle> => {
  const response = await api.post(`/knowledge-base/${id}/publish`);
  await mutate(`/knowledge-base/${id}`);
  await mutate('/knowledge-base');
  return response.data;
};

// Archive an article
export const archiveKnowledgeBaseArticle = async (id: number): Promise<KnowledgeBaseArticle> => {
  const response = await api.post(`/knowledge-base/${id}/archive`);
  await mutate(`/knowledge-base/${id}`);
  await mutate('/knowledge-base');
  return response.data;
};
